import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import SEO from "../../components/SEO";
import { PageHero, EmptyState } from "../../components/Bits";

export default function Search() {
  const { t } = useApp();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState(null);

  const run = async (query) => {
    if (!query.trim()) return;
    try { const r = await api.get(`/public/search?q=${encodeURIComponent(query)}`); setResults(r.data); }
    catch (e) { setResults({ articles: [], projects: [], news: [] }); }
  };

  useEffect(() => { if (params.get("q")) run(params.get("q")); // eslint-disable-next-line
  }, []);

  const Section = ({ title, items, prefix, nameKey }) => items.length > 0 && (
    <div className="mt-10">
      <h2 className="font-display text-2xl font-bold text-navy">{title}</h2>
      <div className="mt-4 space-y-3">
        {items.map((it) => (
          <Link key={it.id} to={`/${prefix}/${it.slug}`} className="card-ssf block p-5 transition-transform duration-200 hover:-translate-y-0.5" data-testid={`search-${prefix}-${it.id}`}>
            <h3 className="font-semibold text-navy">{it[nameKey] || it.title || it.title_en}</h3>
            {(it.excerpt || it.short_desc_en || it.description) && <p className="mt-1 text-sm text-slate-500">{(it.excerpt || it.short_desc_en || it.description).slice(0, 140)}</p>}
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="search-page">
      <SEO title={t("search")} />
      <PageHero title={t("search")} />
      <section className="py-16">
        <div className="container-ssf max-w-3xl">
          <form onSubmit={(e) => { e.preventDefault(); setParams({ q }); run(q); }} className="flex gap-3" data-testid="search-form">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} data-testid="search-input"
              className="flex-1 rounded-full border border-gray-200 bg-white px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
            <button type="submit" className="btn-primary !px-6" data-testid="search-submit"><SearchIcon size={17} /></button>
          </form>
          {results && (
            <div data-testid="search-results">
              <Section title={t("articles")} items={results.articles} prefix="articles" />
              <Section title={t("projects")} items={results.projects} prefix="projects" />
              {results.news.length > 0 && (
                <div className="mt-10">
                  <h2 className="font-display text-2xl font-bold text-navy">{t("news")}</h2>
                  <div className="mt-4 space-y-3">
                    {results.news.map((n) => (
                      <Link key={n.id} to="/news" className="card-ssf block p-5" data-testid={`search-news-${n.id}`}>
                        <h3 className="font-semibold text-navy">{n.title}</h3>
                        <p className="mt-1 text-sm text-slate-500">{(n.description || "").slice(0, 140)}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {results.articles.length + results.projects.length + results.news.length === 0 && (
                <div className="mt-10"><EmptyState text="No results found." /></div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
